import { AuthorizationParams } from "@auth0/auth0-spa-js";
import { Config } from "../entities/config";
import {
  DEFAULT_UNREAD_MESSAGES_COUNT,
  EventActions,
  LOGGED_IN_USER_NO_NAME_STATUS,
  LOGGED_OUT_USER_STATUS,
} from "../utility/constants";
import { NaviError, NaviErrorTypes } from "../utility/navi-error";
import { GlobalDataLayerService } from "./analytics/global-data-layer-service";
import { getUserInformation } from "./user-information-service";
import { finderStateStore } from "../state/finder-state-store";
import { navStateStore } from "../state/nav-state-store";
import { UserData } from "../entities/user-information";
import { IsLoggedIn, isString } from "../utility/helper";
import {
  getAuth0Client,
  getLogoutUrl,
  getRedirectUrl,
  mapAuthLocaleOptions,
} from "./auth-client-service";
import windowService from "./window-service";
import {
  dispatchAuthEvent,
  dispatchLegacyGetTokenEvent,
  PHN_AUTH_EVENT,
} from "./auth-event.service";
import logger from "./logger";
import { enqueueEvent } from "./event-queue.service";
import { NavigationPageAction } from "./micro-agent-wrapper";

enum FLOW_PROCESS {
  LOGIN = "login",
  LOGOUT = "logout",
}

export function getUserNameFromUserData(user: UserData | null): string {
  if (!user) return "";
  const { firstName, lastName }: UserData = user;
  return [firstName, lastName].filter(isString).join(" ");
}

export async function initializeUserData(
  accessToken: string,
  locale: string,
): Promise<void> {
  let loggedInState = LOGGED_OUT_USER_STATUS;
  let userId: string;
  let ciamid: string;

  try {
    const bffData = await getUserInformation(accessToken, locale);

    if (bffData === undefined || bffData === null) {
      throw new NaviError(
        `Invalid user data object extracted from user profile response`,
        NaviErrorTypes.NO_RESULTS,
      );
    }

    finderStateStore.state.savedSearchesCount = bffData.savedSearches;
    finderStateStore.state.savedVehiclesCount = bffData.savedVehicles;
    navStateStore.state.unreadMessagesCount = bffData.unreadMessages;
    const { userData } = bffData;

    loggedInState =
      getUserNameFromUserData(userData) || LOGGED_IN_USER_NO_NAME_STATUS;
    userId = bffData.userData.userId ?? "";
    ciamid = bffData.userData.ciamId ?? "";
    if (loggedInState) {
      GlobalDataLayerService.getInstance().setUser(
        userId,
        ciamid,
        IsLoggedIn(loggedInState),
      );
      GlobalDataLayerService.getInstance().pushDataLayerEvent(
        EventActions.IDS_LOAD,
      );
    }

    dispatchAuthEvent({
      eventType: PHN_AUTH_EVENT.UPDATED_USER_STATE,
      state: loggedInState,
    });
  } catch (err) {
    if (
      !(err instanceof NaviError) ||
      err.type !== NaviErrorTypes.UNAUTHORIZED
    ) {
      loggedInState = LOGGED_IN_USER_NO_NAME_STATUS;
    }

    navStateStore.state.unreadMessagesCount = DEFAULT_UNREAD_MESSAGES_COUNT;
    navStateStore.state.loggedInState = loggedInState;

    dispatchAuthEvent({
      eventType: PHN_AUTH_EVENT.UPDATED_USER_STATE_ERROR,
      state: loggedInState,
      error: err as Error,
    });
    throw err;
  }

  navStateStore.state.loggedInState = loggedInState;
  GlobalDataLayerService.getInstance().setUser(
    userId,
    ciamid,
    IsLoggedIn(loggedInState),
  );
}

export async function logout(config: Config): Promise<void> {
  const auth0Client = getAuth0Client(config);
  const logoutUrl = getLogoutUrl(config);

  dispatchAuthEvent({ eventType: PHN_AUTH_EVENT.PREPARE_LOGOUT });
  startFlow(FLOW_PROCESS.LOGOUT);

  await auth0Client.logout({
    openUrl() {
      windowService.redirectToUrl(logoutUrl);
    },
  });
}

function startFlow(process: FLOW_PROCESS): void {
  sessionStorage.setItem("phn-flow", `navi-${process}`);
}

function getFlowProcess(): FLOW_PROCESS | null {
  const flow = sessionStorage.getItem("phn-flow")?.replace("navi-", "");
  if (flow === FLOW_PROCESS.LOGIN || flow === FLOW_PROCESS.LOGOUT) {
    return flow;
  }
  return null;
}

function endFlow(): void {
  sessionStorage.removeItem("phn-flow");
}

export async function loginWithRedirect(config: Config): Promise<void> {
  const auth0Client = getAuth0Client(config);
  const { authOptions, locale } = navStateStore.state;
  const authLocaleOptions = mapAuthLocaleOptions(locale);
  startFlow(FLOW_PROCESS.LOGIN);
  enqueueEvent({ type: NavigationPageAction.NAVIGATION_LOGIN });

  dispatchAuthEvent({ eventType: PHN_AUTH_EVENT.PREPARE_LOGIN_WITH_REDIRECT });
  const authorizationParams: AuthorizationParams = {
    ...authLocaleOptions,
    redirect_uri: getRedirectUrl(
      config.IDENTITY_REDIRECT_URI,
      authOptions?.redirectUrl,
    ),
  };

  try {
    await auth0Client.loginWithRedirect({
      appState: {
        path: window.location.pathname,
        query: window.location.search,
      },
      authorizationParams,
    });
  } catch (error: unknown) {
    const unknownError = new Error(
      "loginWithRedirect failed with an unknown error",
    );
    const loggerError = error instanceof Error ? error : unknownError;
    enqueueEvent({
      type: NavigationPageAction.NAVIGATION_LOGIN_ERROR,
      options: loggerError,
    });
    logger.error(`PHN_AUTHENTICATION_ERROR`, loggerError);
    endFlow();
    throw loggerError;
  }
}

export async function getTokenSilently(
  config: Config,
): Promise<string | { token: null; error: string }> {
  try {
    dispatchAuthEvent({ eventType: PHN_AUTH_EVENT.PREPARE_GET_TOKEN_SILENTLY });
    const auth0Client = getAuth0Client(config);
    const token = await auth0Client.getTokenSilently({
      authorizationParams: {
        audience: config.IDENTITY_AUDIENCE,
        scope: config.IDENTITY_USER_SCOPES,
      },
    });

    // @deprecated kept for legacy consumers
    dispatchLegacyGetTokenEvent(token);
    dispatchAuthEvent({ eventType: PHN_AUTH_EVENT.TOKEN_RECEIVED, token });
    if (getFlowProcess() === FLOW_PROCESS.LOGIN) {
      enqueueEvent({ type: NavigationPageAction.NAVIGATION_LOGIN_SUCCESS });
      endFlow();
    }

    return token;
  } catch (err) {
    if (getFlowProcess() === FLOW_PROCESS.LOGIN) {
      enqueueEvent({
        type: NavigationPageAction.NAVIGATION_LOGIN_ERROR,
        options: err,
      });

      // Track errors from the auth-callback page for analysis as the page itself does not have NR Agent
      const redirectErrorKey = "phn-login-redirect-error";
      const redirectError = sessionStorage.getItem(redirectErrorKey);
      if (redirectError) {
        enqueueEvent({
          type: NavigationPageAction.NAVIGATION_LOGIN_REDIRECT_ERROR,
          options: redirectError,
        });

        sessionStorage.removeItem(redirectErrorKey);
      }
    } else if (getFlowProcess() === FLOW_PROCESS.LOGOUT) {
      enqueueEvent({ type: NavigationPageAction.NAVIGATION_LOGOUT_SUCCESS });
    }
    endFlow();
    dispatchAuthEvent({
      eventType: PHN_AUTH_EVENT.TOKEN_RECEIVED_ERROR,
      error: err as Error,
    });

    return {
      token: null,
      error: JSON.stringify(err),
    };
  }
}
